import * as PIXI from 'pixi.js';

/**
 * Load a texture with fallback mechanisms
 * @param primaryPath - The primary path to the texture
 * @param fallbackExtension - The extension to try if primary loading fails (e.g., 'png')
 * @param app - PIXI Application for creating emergency fallbacks
 * @param fallbackColor - Color to use for emergency fallback (hex value)
 * @param fallbackShape - Shape to use for emergency fallback ('circle' or 'rect')
 * @param fallbackSize - Size of the emergency fallback shape
 * @returns The loaded texture
 */
export const loadTextureWithFallback = async (
  primaryPath: string,
  fallbackExtension: string,
  app: PIXI.Application | null,
  fallbackColor: number,
  fallbackShape: 'circle' | 'rect',
  fallbackSize: number
): Promise<PIXI.Texture> => {
  try {
    try {
      const texture = await PIXI.Assets.load(primaryPath);
      console.log(`Loaded texture successfully: ${primaryPath}`);
      return texture;
    } catch (primaryError) {
      console.warn(
        `Failed to load primary texture, trying fallback: ${primaryError}`
      );

      // Replace extension for fallback (e.g. .svg to .png)
      const lastDotIndex = primaryPath.lastIndexOf('.');
      if (lastDotIndex === -1) {
        throw new Error(`Invalid texture path: ${primaryPath}`);
      }

      const fallbackPath = `${primaryPath.substring(0, lastDotIndex)}.${fallbackExtension}`;
      const fallbackTexture = await PIXI.Assets.load(fallbackPath);
      console.log(`Loaded fallback texture successfully: ${fallbackPath}`);
      return fallbackTexture;
    }
  } catch (error) {
    console.warn(`All texture loading attempts failed: ${error}`);

    // Create emergency fallback texture
    if (app && app.renderer) {
      const graphics = new PIXI.Graphics();
      graphics.beginFill(fallbackColor);

      if (fallbackShape === 'circle') {
        graphics.drawCircle(0, 0, fallbackSize);
      } else {
        // For rectangle, size is the width/height, so half of it is the offset
        const halfSize = fallbackSize / 2;
        graphics.drawRect(-halfSize, -halfSize, fallbackSize, fallbackSize);
      }

      graphics.endFill();
      return app.renderer.generateTexture(graphics);
    }

    // Last resort if renderer is not available
    return PIXI.Texture.EMPTY;
  }
};
